// /Design/DesignSettings.js
// Client-facing page (feature "deviceConfig.design"): the client maintains the
// default images and default animations for the SelfCheckin phases himself.
// The defaults are used whenever a DeviceConfig phase has no own values;
// an empty default image means no image, an empty default animation falls
// back to the built-in app animation.
import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Form, Input, Card, Space, Button, Switch, Typography, Tooltip, Image, message } from 'antd';
import { ReloadOutlined, InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Player } from '@lottiefiles/react-lottie-player';
import axios from '../../config/axiosInstance';

// Defaults (local assets), same as in the DeviceConfig Animations tab
import success from '../../assets/animations/success.json';
import printing from '../../assets/animations/printing.json';
import waiting from '../../assets/animations/scan_qrcode.json';
import denied from '../../assets/animations/denied.json';
import gear from '../../assets/animations/gear.json';

const { Title, Text } = Typography;

const PHASES = [
    { key: 'waiting', label: 'Waiting', help: 'Standby screen/display QR.', defaultJson: waiting },
    { key: 'checking', label: 'Checking', help: 'Checking permissions/entry.', defaultJson: gear },
    { key: 'printing', label: 'Printing', help: 'Printing badge.', defaultJson: printing },
    { key: 'success', label: 'Success', help: 'Success', defaultJson: success },
    { key: 'denied', label: 'Denied', help: 'Access denied/blacklisted/error.', defaultJson: denied },
];

// URL points to a static/animated image instead of a Lottie JSON
const isImageUrl = (v) => /\.(png|gif)$/i.test(v || '');

// http(s) URL ending in .json/.png/.gif; empty = app default
const isValidAnimationUrl = (v) => {
    if (!v) return true;
    try {
        const u = new URL(v);
        const path = u.pathname.toLowerCase();
        return ['http:', 'https:'].includes(u.protocol)
            && (path.endsWith('.json') || path.endsWith('.png') || path.endsWith('.gif'));
    } catch {
        return false;
    }
};

// A small card with input + live preview, same look as the Animations tab
const DefaultAnimationCard = ({ form, phase }) => {
    const value = Form.useWatch(phase.key, form);
    const src = value && isValidAnimationUrl(value) ? value : phase.defaultJson;
    const showImagePreview = typeof src === 'string' && isImageUrl(src);

    return (
        <Card
            size="small"
            style={{ height: '100%' }}
            title={
                <Space align="center">
                    {phase.label}
                    <Tooltip title={phase.help}>
                        <InfoCircleOutlined />
                    </Tooltip>
                </Space>
            }
            extra={
                <Button icon={<ReloadOutlined />} onClick={() => form.setFieldsValue({ [phase.key]: '' })}>
                    Reset
                </Button>
            }
        >
            <Form.Item
                name={phase.key}
                label="Animation URL"
                tooltip="Paste a direct URL to a Lottie .json, .png or .gif file (optional). If left blank, the built-in default animation is used."
                rules={[
                    {
                        validator: (_, val) =>
                            isValidAnimationUrl(val)
                                ? Promise.resolve()
                                : Promise.reject(new Error('It must be an http(s) URL ending in .json, .png or .gif.')),
                    },
                ]}
            >
                <Input placeholder="https://.../animation.json|.png|.gif " />
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
                {showImagePreview ? (
                    <Image src={src} alt="preview" height={220} style={{ maxWidth: 220, objectFit: 'contain' }} />
                ) : (
                    <Player autoplay loop src={src} style={{ width: 220, height: 220 }} />
                )}
            </div>

            <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Text type="secondary">{value ? 'Preview (remote)' : 'Preview (default)'}</Text>
            </div>
        </Card>
    );
};

const DesignSettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const useBackground = Form.useWatch(['defaultImages', 'isBackgroundImageEnabled'], form) === true;
    const bannerUrl = Form.useWatch(['defaultImages', 'bannerUrl'], form);
    const backgroundUrl = Form.useWatch(['defaultImages', 'backgroundUrl'], form);
    const shownImageUrl = useBackground ? backgroundUrl : bannerUrl;

    const fetchDefaults = useCallback(async () => {
        try {
            const [animations, images] = await Promise.all([
                axios.get('/clients/design/default-animations'),
                axios.get('/clients/design/default-images'),
            ]);
            form.setFieldsValue({
                ...(animations.data.defaultAnimations || {}),
                defaultImages: images.data.defaultImages || {},
            });
        } catch (error) {
            console.error('Error loading design settings:', error);
            message.error('Error loading the design settings.');
        } finally {
            setLoading(false);
        }
    }, [form]);

    useEffect(() => {
        fetchDefaults();
    }, [fetchDefaults]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            const { defaultImages, ...defaultAnimations } = values;
            await axios.put('/clients/design/default-images', { defaultImages });
            await axios.put('/clients/design/default-animations', { defaultAnimations });

            message.success('Design settings saved successfully.');
        } catch (error) {
            if (error?.errorFields) return; // validation error, already shown at the fields
            console.error('Error saving design settings:', error);
            message.error(error?.response?.data?.message || 'Error saving the design settings.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24 }}>
                <h1 style={{ margin: 0 }}>Design</h1>
            </div>

            <Form form={form} layout="vertical">
                <Card
                    style={{ width: '100%', marginBottom: 24 }}
                    title={<Title level={4} style={{ margin: 0 }}>Default images</Title>}
                    loading={loading}
                >
                    <Row style={{ marginBottom: 16 }}>
                        <Col span={24}>
                            <Text type="secondary">
                                This image is used on every check-in phase that has no own image in its
                                Device Config: either a banner at the top or a fullscreen background.
                            </Text>
                        </Col>
                    </Row>

                    <Row>
                        <Col span={24}>
                            <Form.Item
                                name={['defaultImages', 'isBackgroundImageEnabled']}
                                valuePropName="checked"
                                label={
                                    <>
                                        Fullscreen background
                                        <Tooltip title="Use a fullscreen background image (JPG, PNG, GIF) instead of the banner at the top.">
                                            <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                                        </Tooltip>
                                    </>
                                }
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                            {useBackground ? (
                                <Form.Item name={['defaultImages', 'backgroundUrl']} label="Background URL">
                                    <Input placeholder="https://.../background.jpg|.png|.gif" />
                                </Form.Item>
                            ) : (
                                <Form.Item name={['defaultImages', 'bannerUrl']} label="Banner URL">
                                    <Input placeholder="https://.../banner.jpg|.png|.gif" />
                                </Form.Item>
                            )}
                        </Col>
                        <Col xs={24} sm={12}>
                            {shownImageUrl && (
                                <Image src={shownImageUrl} alt="preview" style={{ maxWidth: '100%', height: 'auto', marginTop: 8 }} />
                            )}
                        </Col>
                    </Row>
                </Card>

                <Card
                    style={{ width: '100%' }}
                    title={<Title level={4} style={{ margin: 0 }}>Default animations</Title>}
                    loading={loading}
                >
                    <Row style={{ marginBottom: 24 }}>
                        <Col span={24}>
                            <Text type="secondary">
                                These animations are used on all your devices whenever a Device Config has no own
                                animation URL. Leave a field empty to keep the built-in default animation.
                            </Text>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        {PHASES.map((phase) => (
                            <Col xs={24} md={12} lg={8} key={phase.key}>
                                <DefaultAnimationCard form={form} phase={phase} />
                            </Col>
                        ))}
                    </Row>
                </Card>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default DesignSettings;
